import { useMemo, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'
import DatePickerISO from 'ui/components/DatePickerISO'
import ViiluDialog from 'ui/components/ViiluDialog'
import { ErrorMessage } from 'ui/ManagerFinance/utils/components'
import { _generateLoanShareCalculationPdf } from 'state/finance-loan.actions'
import type {
	Loan,
	LoanShareholderWithApartment,
} from '@viilu-web/shared/types/loans'
import { useDispatch } from 'react-redux'
import * as notifyActions from 'state/notifyActions'
import { CircularProgress } from 'react-md/lib/Progress'
import { FontIcon } from 'react-md'

const ContentContainer = styled.div`
	display: flex;
	flex-direction: column;
	padding: 0 1.5rem;
	margin-top: 1rem;
	margin-bottom: 1rem;
	overflow: visible;
`

const PickersContainer = styled.div`
	display: flex;
	flex-direction: row;
	gap: 1rem;
`

const DatePickerInputContainer = styled.div`
	display: flex;
	flex-direction: column;
	gap: 0.5rem;
`

const ShareholderListContainer = styled.div`
	margin-top: 1.5rem;
	max-height: 300px;
	overflow-y: auto;
	border: 1px solid #eee;
	border-radius: 4px;
	background: #fafafa;
	padding: 0.5rem 1rem;
`

type Props = {
	onHide: () => void
	loan: Loan
	initialStartDate?: string
	initialEndDate?: string
	selectedShareholders: LoanShareholderWithApartment[]
}

function LoanShareCalculationCreationDialog({
	onHide,
	loan,
	initialStartDate,
	initialEndDate,
	selectedShareholders,
}: Props) {
	const intl = useIntl()
	const dispatch = useDispatch()
	const [loading, setLoading] = useState(false)
	const [options, setOptions] = useState({
		startDate: initialStartDate || undefined,
		endDate: initialEndDate || '',
	})
	const [reportURL, setReportURL] = useState<string | null>(null)

	const generateLoanShareCalculationPdf = async () => {
		try {
			if (loading) return
			setLoading(true)
			const response = await _generateLoanShareCalculationPdf(loan.uuid, {
				companyUUID: loan.companyUUID,
				startDate: options.startDate ? options.startDate : undefined,
				endDate: options.endDate,
				shareholdersWithApartment: selectedShareholders.map((shareholder) => ({
					uuid: shareholder.uuid,
					apartment: {
						uuid: shareholder.apartment.uuid ?? '',
						identifier: shareholder.apartment?.identifier ?? '',
						label: shareholder.apartment?.label ?? '',
					},
				})),
			})
			if (response?.ok) {
				const result = await response.json()
				if (result?.url) {
					setReportURL(result.url)
				}
				setLoading(false)
			} else {
				dispatch(
					notifyActions.error(
						intl.formatMessage({
							defaultMessage: 'Lainaosuuslaskelman luonti ei onnistunut.',
							description:
								'Notification message when there was error during loan share calculation PDF generation.',
						}),
					),
				)
				throw new Error('Response is not ok.')
			}
		} catch (error) {
			console.error('Error generating loan share calculation PDF:', error)
			setLoading(false)
		}
	}

	const endDateError = useMemo(() => {
		if (!options.endDate) {
			return intl.formatMessage({
				defaultMessage: 'Loppupäivä on pakollinen.',
				description: 'Error message when end date is not provided.',
			})
		}
		if (options.startDate && options.endDate) {
			const start = new Date(options.startDate)
			const end = new Date(options.endDate)
			if (start > end) {
				return intl.formatMessage({
					defaultMessage: 'Loppupäivä ei voi olla ennen alkupäivämäärää.',
					description: 'Error message when end date is before start date.',
				})
			}
		}
		return ''
	}, [options.startDate, options.endDate, intl])

	const actions = [
		{
			id: 'dialog-cancel',
			children: intl.formatMessage({
				defaultMessage: 'Peruuta',
				description: 'Cancel button',
			}),
			onClick: onHide,
		},
		reportURL
			? {
					id: 'dialog-download',
					secondary: true,
					children: (
						<span
							style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
						>
							<FontIcon inherit>article</FontIcon>
							{intl.formatMessage({
								defaultMessage: 'Lataa raportti',
								description: 'Download report button',
							})}
						</span>
					),
					onClick: () => window.open(reportURL, '_blank'),
			  }
			: {
					id: 'dialog-ok',
					primary: true,
					disabled: endDateError,
					children: loading ? (
						<CircularProgress id="progress" style={{ marginTop: 0 }} />
					) : (
						intl.formatMessage({
							defaultMessage: 'Luo raportti',
							description: 'Save button',
						})
					),
					onClick: () => generateLoanShareCalculationPdf(),
			  },
	]

	return (
		<ViiluDialog
			id="loan-event-create-dialog"
			title={intl.formatMessage({
				defaultMessage: 'Lainaosuuslaskelman muodostaminen',
				description: 'Title of create new event for loan',
			})}
			footerStyle={{ padding: '1rem' }}
			visible={true}
			actions={actions}
			onHide={onHide}
			focusOnMount={false}
			containFocus={false}
			dialogClassName="responsive-dialog"
			contentClassName="responsive-dialog-content"
			autosizeContent={false}
			modal
			portal
			paddedContent
		>
			<ContentContainer>
				<PickersContainer>
					<DatePickerInputContainer>
						<DatePickerISO
							id="loan-share-calculation-dialog-start-date-filter"
							portal
							renderNode={document.getElementById('root-dialog') || undefined}
							label={intl.formatMessage({
								defaultMessage: 'Laskelman alkupäivä',
								description: 'Label for the loan share calculation start date.',
							})}
							value={options.startDate || ''}
							onChange={(isoDate: string) => {
								setOptions((prev) => ({
									...prev,
									startDate: isoDate,
								}))
								setReportURL(null)
							}}
						/>
					</DatePickerInputContainer>
					<DatePickerInputContainer>
						<DatePickerISO
							id="loan-share-calculation-dialog-end-date-filter"
							portal
							renderNode={document.getElementById('root-dialog') || undefined}
							label={intl.formatMessage({
								defaultMessage: 'Laskelman loppupäivä',
								description: 'Label for the loan share calculation end date.',
							})}
							value={options.endDate}
							onChange={(isoDate: string) => {
								setOptions((prev) => ({
									...prev,
									endDate: isoDate,
								}))
								setReportURL(null)
							}}
						/>
						{endDateError && <ErrorMessage>{endDateError}</ErrorMessage>}
					</DatePickerInputContainer>
				</PickersContainer>
				<ShareholderListContainer>
					<p>
						<FormattedMessage
							defaultMessage="Olet valinnut seuraavat huoneistot/osakeryhmät lainaosuuslaskelmalle:"
							description="You have selected the following sharegroups for the loan share calculation"
						/>
					</p>
					<ul>
						{selectedShareholders.map((shareholder) => (
							<li key={shareholder.uuid}>
								<p>{shareholder.apartment?.label || shareholder.uuid}</p>
							</li>
						))}
					</ul>
				</ShareholderListContainer>
			</ContentContainer>
		</ViiluDialog>
	)
}

export default LoanShareCalculationCreationDialog
